import { Link, useLocation } from "react-router-dom";
import { Play } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import SearchBar from "@/components/SearchBar";
import ReturnToStudy from "@/components/ReturnToStudy";
import { useDemo } from "@/demo/DemoProvider";
import AccountBadge from "@/auth/AccountBadge";

export default function Layout({ children, onSelectKanji }) {
  const location = useLocation();
  const demo = useDemo();
  const navItem = (to, label, testId) => {
    const active = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
    return (
      <Link
        to={to}
        data-testid={testId}
        className={`px-3 md:px-4 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] font-sans transition-colors border-l border-borderDefault first:border-l-0 ${
          active ? "bg-sumi text-washi" : "bg-transparent text-sumi hover:text-shu"
        }`}
      >
        {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-washi text-sumi">
      <header
        data-testid={TEST_IDS.appHeader}
        className="flex items-center justify-between gap-2 md:gap-3 border-b border-borderDefault px-3 md:px-8 py-2.5 md:py-4 bg-washi/85 backdrop-blur-sm sticky top-0 z-30"
      >
        <Link to="/" data-testid={TEST_IDS.appTitle} className="font-serif text-lg md:text-3xl tracking-tight whitespace-nowrap shrink-0">
          漢<span className="text-shu">字</span>
          <span className="text-sumiSoft text-[10px] md:text-base font-sans ml-1.5 md:ml-2 align-middle hidden sm:inline">
            build path
          </span>
        </Link>

        <nav className="flex items-center border border-borderDefault shrink-0">
          {navItem("/", "Home", TEST_IDS.navBuild)}
          {navItem("/study", "Study", TEST_IDS.navStudy)}
          {navItem("/journal", "Journal", TEST_IDS.navJournal)}
          {navItem("/guide", "Guide", "nav-guide")}
        </nav>

        {demo?.status === "idle" && (
          <button
            data-testid="demo-start"
            onClick={() => demo.controls.start()}
            className="flex items-center gap-1.5 border border-shu text-shu px-3 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] font-sans hover:bg-shu hover:text-washi transition-colors shrink-0"
          >
            <Play size={12} fill="currentColor" strokeWidth={0} /> 90-sec demo
          </button>
        )}

        <SearchBar onSelect={onSelectKanji} />
        <AccountBadge />
      </header>

      <main className="flex-1 w-full">{children}</main>

      <ReturnToStudy />

      <footer
        data-testid="app-footer"
        className="border-t border-borderDefault px-3 md:px-8 py-3 flex items-center justify-between gap-2 text-[10px] uppercase tracking-[0.25em] font-sans text-sumiSoft"
      >
        <div className="flex items-center gap-4">
          <span>漢字 · build path</span>
          <Link
            to="/guide"
            data-testid="footer-guide-link"
            className="hover:text-shu transition-colors"
          >
            user guide →
          </Link>
          <Link
            to="/welcome"
            data-testid="footer-welcome-link"
            className="hover:text-shu transition-colors"
          >
            why kanji build →
          </Link>
        </div>
        <a
          href="https://app.emergent.sh/?utm_source=kanji-build-path"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-emergent"
          className="hover:text-shu transition-colors"
        >
          made with emergent
        </a>
      </footer>
    </div>
  );
}
